import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { NotificationBanner, NotificationType } from '@/Components/shared/NotificationBanner';

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  autoDismiss?: boolean;
  dismissAfter?: number;
}

interface NotificationContextType {
  showNotification: (type: NotificationType, message: string, autoDismiss?: boolean, dismissAfter?: number) => void;
  hideNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const hideNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback(
    (type: NotificationType, message: string, autoDismiss = true, dismissAfter = 5000) => {
      const id = `notification-${Date.now()}-${Math.random()}`;
      const newNotification: Notification = {
        id,
        type,
        message,
        autoDismiss,
        dismissAfter,
      };

      setNotifications((prev) => [...prev, newNotification]);

      if (autoDismiss) {
        setTimeout(() => {
          hideNotification(id);
        }, dismissAfter);
      }
    },
    [hideNotification]
  );

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification }}>
      {children}

      {notifications.length > 0 && (
        <div className="tw-fixed tw-top-20 tw-right-4 tw-z-50 tw-space-y-3 tw-max-w-md">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="tw-animate-slide-in-right tw-shadow-lg"
            >
              <NotificationBanner
                type={notification.type}
                message={notification.message}
                onClose={() => hideNotification(notification.id)}
                autoDismiss={notification.autoDismiss}
                dismissAfter={notification.dismissAfter}
              />
            </div>
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
};
